import { useState, useMemo, useEffect } from "react";
import Button from "@cloudscape-design/components/button";
import Modal from "@cloudscape-design/components/modal";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Alert from "@cloudscape-design/components/alert";
import { QbilonService } from "../../services/qbilon-service";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Select from "@cloudscape-design/components/select";

export default function CreateRelationshipModal({
    onOpenSettings,
    onDismiss,
    onSave,
    providerNodes,
    serviceNodes
}) {

    const [isLoading, setIsLoading] = useState(false);
    const [selectedType,setSelectedType] = useState({ label: 'offers', id: 3 });
    const [selectedProvider,setSelectedProvider] = useState();
    const [selectedService,setSelectedService] = useState();
    const [error, setError] = useState("");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const isValid = selectedProvider && selectedService && selectedType;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Memoized sorted provider and service lists
    const sortedProviders = useMemo(() =>
        [...providerNodes].sort((a, b) => a.name.localeCompare(b.name)).map((node) => ({
            label: node.name,
            value: node.qbilonId
        })),
        [providerNodes]
    );

    const sortedServices = useMemo(() =>
        [...serviceNodes].sort((a, b) => a.name.localeCompare(b.name)).map((node) => ({
            label: node.name,
            value: node.qbilonId
        })),
        [serviceNodes]
    );

    return (
        <>
            <Modal
                visible={onOpenSettings}
                onDismiss={onDismiss}
                header="Neue Beziehung anlegen"
                closeAriaLabel="Dialog schließen"
                size={isMobile ? "max" : "medium"}
            >
                <SpaceBetween size="m" direction="vertical">
                    <form onSubmit={async e => {
                        e.preventDefault()

                        // Validation
                        if (!selectedProvider || !selectedService || !selectedType) {
                            setError("Bitte füllen Sie alle Pflichtfelder aus.");
                            return;
                        }

                        setError("");
                        setIsLoading(true);

                        try {
                            await QbilonService.createNewEdge(selectedProvider.value, selectedService.value, selectedType.label);
                            onSave?.();
                            setSelectedType({ label: 'offers', id: 3 })
                            setSelectedProvider(null)
                            setSelectedService(null)
                        } catch (err) {
                            setError(err?.message ?? "Fehler beim Erstellen der Beziehung");
                        } finally {
                            setIsLoading(false);
                        }
                    }}>
                        <Form
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button key="cancel" formAction="none" variant="link" onClick={onDismiss} disabled={isLoading}>
                                        {isLoading ? <Spinner aria-label="Lädt..." /> : "Abbrechen"}
                                    </Button>
                                    <Button key="submit" variant="primary" disabled={!isValid || isLoading}>{isLoading ? <Spinner aria-label="Speichert..." /> : "Speichern"}</Button>
                                </SpaceBetween>
                            }
                        >
                            <SpaceBetween direction="vertical" size="l">
                                {error && (
                                    <Alert key="error-alert" type="error" dismissible onDismiss={() => setError("")}>
                                        {error}
                                    </Alert>
                                )}
                                <FormField key="provider-field" label="Provider" constraintText="Pflichtfeld">
                                    <Select
                                    selectedOption={selectedProvider}
                                        onChange={({ detail }) =>
                                            setSelectedProvider(detail.selectedOption)
                                        }
                                    options={sortedProviders}
                                    />
                                </FormField>
                                <FormField key="service-field" label="Technische Service Offering" constraintText="Pflichtfeld">
                                    <Select
                                    selectedOption={selectedService}
                                        onChange={({ detail }) =>
                                            setSelectedService(detail.selectedOption)
                                        }
                                    options={sortedServices}
                                    />
                                </FormField>
                                <FormField key="type-field" label="Typ" constraintText="Pflichtfeld">
                                    <Select
                                        selectedOption={selectedType}
                                        onChange={({ detail }) =>
                                            setSelectedType(detail.selectedOption)
                                        }
                                        options={[
                                            { label: 'onboarding', value: 1 },
                                            { label: 'offboarding', value: 2 },
                                            { label: 'offers', value: 3 },
                                        ]} />
                                </FormField>
                            </SpaceBetween>
                        </Form>
                    </form>
                </SpaceBetween>
            </Modal>
        </>
    );
}
